import React, { useState, useCallback, useContext } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  useToast,
  Icon,
  Flex,
  Code,
  Divider,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';

const FqIntake = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { accessToken } = useContext(AppContext);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file || !accessToken) return;

    setIsUploading(true);
    setParsedData(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await callApi('/api/fq/upload-edi', accessToken, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to parse EDI file');
      }

      setParsedData(result.data);
      toast({
        title: 'File Parsed Successfully',
        description: `Extracted data from ${result.filename}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  }, [toast, accessToken]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    multiple: false 
  });

  return (
    <Box maxW="7xl" mx="auto">
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>EDI Intake</Heading>
          <Text color="gray.500">
            Upload an 837P (Claim) or 835 (Remittance) file to normalize it into the FQHC canonical schema.
          </Text>
        </Box>

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          p={10}
          bg={isDragActive ? 'blue.50' : bgColor}
          border="2px dashed"
          borderColor={isDragActive ? 'blue.400' : borderColor}
          borderRadius="xl"
          textAlign="center"
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
        >
          <input {...getInputProps()} />
          <Icon as={FiUploadCloud} w={12} h={12} color="blue.500" mb={4} />
          <Heading size="md" mb={2}>
            {isDragActive ? 'Drop the EDI file here' : 'Drag & Drop EDI File'}
          </Heading>
          <Text color="gray.500">or click to select a file from your computer</Text>
          <Button mt={6} colorScheme="blue" isLoading={isUploading}>
            Select File
          </Button>
        </Box>

        {/* Results View */}
        {parsedData && (
          <Box bg={bgColor} p={6} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
            <Flex align="center" mb={6}>
              <Icon as={FiCheckCircle} color="green.500" w={6} h={6} mr={3} />
              <Heading size="md">Canonical Schema Generated</Heading>
              <Badge ml={4} colorScheme={parsedData.type === '837P' ? 'purple' : 'green'}>
                {parsedData.type} Detected
              </Badge>
            </Flex>
            
            <Divider mb={6} />
            
            <Box bg="gray.900" p={4} borderRadius="md" overflowX="auto">
              <Code colorScheme="blackAlpha" color="green.300" bg="transparent" whiteSpace="pre">
                {JSON.stringify(parsedData, null, 2)}
              </Code>
            </Box>
            
            <Flex justify="flex-end" mt={6}>
              <Button 
                colorScheme="blue" 
                rightIcon={<FiFile />}
                onClick={() => navigate('/fq/claim/demo', { state: { claimData: parsedData } })}
              >
                Run FQHC Rules Engine
              </Button>
            </Flex>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default FqIntake;
